import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Feather, X } from 'lucide-react'
import { safeStorage } from '../lib/safeStorage'
import { ReviewComposer } from './ReviewComposer'

// ── ReviewInvite ──────────────────────────────────────────────────────────────
// The emotional trigger: when a user rates a show 4.5–5 reels (the top of the
// scale — they clearly loved it), gently invite them to say WHY in a full review.
// Reviews power Community Reviews, the Reading Room and public profiles, but are
// drastically underused because nothing ever asks. This asks once per show, only
// at the peak of feeling, and is fully dismissible — an invitation, not a chore.
//
// Global (mounted once in App): listens for `nextep:review-invite` {show, stars},
// waits for any rating celebration to settle, then slides up a small card. Tapping
// "Write a review" opens the same ReviewComposer used on the show page.
export function ReviewInvite({ user, profile }) {
  const [invite,   setInvite]   = useState(null)   // { show, stars } awaiting the prompt
  const [composing, setComposing] = useState(null) // { show, stars } when the composer is open
  const [leaving,  setLeaving]   = useState(false)

  useEffect(() => {
    let timer
    const onInvite = (e) => {
      const { show, stars } = e.detail || {}
      if (!user || !show?.id) return
      // Once per show — if they've written, dismissed, or already been asked, skip.
      if (safeStorage.get(`nextep_rvinvite_${show.id}`)) return
      // Let a 5-Reel milestone modal / rating toast land first.
      clearTimeout(timer)
      timer = setTimeout(() => setInvite({ show, stars }), 1400)
    }
    window.addEventListener('nextep:review-invite', onInvite)
    return () => { window.removeEventListener('nextep:review-invite', onInvite); clearTimeout(timer) }
  }, [user])

  const remember = (showId) => { try { safeStorage.set(`nextep_rvinvite_${showId}`, '1') } catch {} }

  const dismiss = () => {
    if (invite) remember(invite.show.id)
    setLeaving(true)
    setTimeout(() => { setInvite(null); setLeaving(false) }, 220)
  }

  const write = () => {
    if (!invite) return
    remember(invite.show.id)
    setComposing(invite)
    setInvite(null)
  }

  return (
    <>
      {invite && createPortal((
        <div style={{
          position:'fixed', left:0, right:0, bottom:'calc(var(--nav-h) + 8px)',
          zIndex:5100, display:'flex', justifyContent:'center', padding:'0 12px',
          pointerEvents:'none',
        }}>
          <div style={{
            pointerEvents:'auto', width:'100%', maxWidth:440,
            background:'var(--sheet)', border:'1px solid rgba(var(--amber-rgb),0.35)',
            borderRadius:'var(--radius-lg, 16px)', boxShadow:'0 14px 40px rgba(0,0,0,0.5)',
            padding:'13px 14px', display:'flex', gap:11, alignItems:'center',
            fontFamily:'var(--font)',
            '--nxa': leaving ? 'rviDown 0.22s ease forwards' : 'rviUp 0.34s var(--ease-spring, cubic-bezier(0.34,1.4,0.5,1))',
          }}>
            <div style={{ width:38, height:38, borderRadius:11, flexShrink:0, background:'rgba(var(--amber-rgb),0.14)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <Feather size={18} color="var(--gold-leaf, var(--amber))"/>
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ color:'var(--text)', fontSize:13.5, fontWeight:600, lineHeight:1.3 }}>
                You loved <span style={{ color:'var(--amber)' }}>{invite.show.name}</span> 🎬
              </div>
              <div style={{ color:'var(--text-dim)', fontSize:12, marginTop:1 }}>Say why in a quick review?</div>
            </div>
            <button onClick={write} style={{ flexShrink:0, background:'var(--amber)', border:'none', borderRadius:'var(--radius-xl, 999px)', color:'var(--amber-ink)', fontWeight:700, fontSize:12.5, padding:'8px 14px', cursor:'pointer', fontFamily:'var(--font)' }}>
              Write it
            </button>
            <button onClick={dismiss} aria-label="Not now" style={{ flexShrink:0, background:'none', border:'none', color:'var(--text-dim)', cursor:'pointer', padding:2, display:'flex' }}>
              <X size={15}/>
            </button>
          </div>
          <style>{`
            @keyframes rviUp   { from{opacity:0;transform:translateY(30px)} to{opacity:1;transform:translateY(0)} }
            @keyframes rviDown { from{opacity:1;transform:translateY(0)} to{opacity:0;transform:translateY(30px)} }
          `}</style>
        </div>
      ), document.body)}

      {composing && (
        <ReviewComposer
          user={user} show={composing.show} profile={profile}
          rating={composing.stars} isNew
          onClose={() => setComposing(null)}
        />
      )}
    </>
  )
}
